/**
 * Maturity and strength display rules shared by the dashboard, the control
 * panel and the target editors (ASTM C1074-19).
 *
 * The backend sends the pour's maturity naming (MaturityDescriptor) and every
 * strength already evaluated against the calibrated range (StrengthReading).
 * These helpers format them, and fall back to the same rules for older
 * responses that lack them:
 *  - Only the exact method "Nurse Saul" is a Temperature-Time Factor
 *    (°C·hrs). Every other stored value ("Arrhenius", "equivalent-age", "",
 *    a group ID, missing) is calculated as Arrhenius, so it is an Equivalent
 *    Age (hrs) at the specified temperature, 20 °C by default.
 *  - Strength always carries the company unit and is never converted.
 *  - A value outside the tested range shows "< S(min)" / "> S(max)" with
 *    OUTSIDE_RANGE_TOOLTIP; a curve without included samples shows "NCS*".
 *
 * Pure functions only (no React, no API calls).
 */
import type {
  CalibratedRange,
  MaturityDescriptor,
  StrengthReading,
} from '../services/pours';
import { strengthUnitFromFormat } from './strengthUnit';

export const NURSE_SAUL = 'Nurse Saul';
export const ARRHENIUS = 'Arrhenius';
export const TTF_UNIT = '°C·hrs';
export const EA_UNIT = 'hrs';
/** Arrhenius specified temperature when the pour does not give one. */
export const DEFAULT_SPECIFIED_TEMP = 20;

export const OUTSIDE_RANGE_TOOLTIP = 'Outside the tested strength-maturity range';
export const NO_SAMPLES_DISPLAY = 'NCS*';
export const NO_SAMPLES_TOOLTIP = 'No calibration samples';
/** Flag text for a target whose strength or maturity lies outside the tested
 *  range. */
export const TARGET_OUTSIDE_RANGE_LABEL = 'Outside tested range';
/** Tooltip for a strength chart point outside the tested range: the curve
 *  there is the calibration's trend extension, not tested data. */
export const TREND_ESTIMATE_TOOLTIP = 'Outside the tested range: trend estimate only';
/** How far a target maturity may sit past an end of the tested range and
 *  still count as inside: thresholds are stored at 1 dp (as the backend's
 *  CalibratedRange.maturity_flag). */
export const TARGET_MATURITY_SLACK = 0.05;
/** The slack as compared, with the backend's float allowance (its
 *  _THRESHOLD_SLACK): 530.05 + 0.05 is 530.0999... in floats, which would
 *  flag a threshold of 530.1 the backend counts as inside. */
const MATURITY_SLACK_COMPARED = TARGET_MATURITY_SLACK + 1e-9;
/** Maturity unit text when the method is not known yet. */
export const MATURITY_UNIT_UNKNOWN = 'TTF (°C·hrs) or EA (hrs)';
/** Maturity axis / field label when the method is not known (a Manual
 *  calibration without a maturity basis). */
export const MATURITY_AXIS_UNKNOWN = 'Maturity (TTF in °C·hrs or EA in hrs)';
/** ASTM C1074 7.3 reading-interval warning (location view). */
export const READING_INTERVAL_WARNING =
  'Readings more than 30 minutes apart in the first 48 hours (or 1 hour after): ' +
  'maturity across these gaps is estimated (ASTM C1074 7.3).';

/** The backend rule (maturity_engine.normalize_method). */
export const normalizeMaturityMethod = (raw?: string | null): 'Nurse Saul' | 'Arrhenius' =>
  raw === NURSE_SAUL ? NURSE_SAUL : ARRHENIUS;

/** 20 -> "20", 2.5 -> "2.5", 23.0 -> "23". */
const formatTemp = (value: number): string => String(Number(value.toFixed(2)));

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** A number, or a decimal the API sent as text ("20.00"); null when blank or
 *  not a number. */
const numeric = (value: unknown): number | null => {
  if (typeof value === 'string' && value.trim() !== '') return finite(Number(value));
  return finite(value);
};

/**
 * `value` rounded half up (away from zero) to `decimals` places, as the
 * backend's round_1dp, which rounds the shortest decimal form of the number:
 * 24.25 -> 24.3 and 1234.45 -> 1234.5 (plain toFixed gives 1234.4).
 */
export const roundHalfUp = (value: number, decimals = 1): number => {
  if (!Number.isFinite(value)) return value;
  const sign = value < 0 ? -1 : 1;
  const shifted = Number(`${Math.abs(value)}e${decimals}`);
  const rounded = Number.isFinite(shifted)
    ? Number(`${Math.round(shifted)}e-${decimals}`)
    : Math.round(Math.abs(value) * 10 ** decimals) / 10 ** decimals;
  return sign * rounded;
};

/** A number as text, rounded half up to `decimals` places (1 dp by default,
 *  the rule for displayed values): 75.55 -> "75.6". */
export const formatHalfUp = (value: number, decimals = 1): string =>
  roundHalfUp(value, decimals).toFixed(decimals);

/** The descriptor the backend would send for a method name (and, for
 *  Arrhenius, a specified temperature: a number, or a decimal as text). */
export const describeMaturity = (
  method?: string | null,
  specifiedTemp?: number | string | null,
): MaturityDescriptor => {
  if (normalizeMaturityMethod(method) === NURSE_SAUL) {
    return {
      method: NURSE_SAUL,
      kind: 'ttf',
      index_short: 'TTF',
      index_label: 'Temperature-Time Factor',
      unit: TTF_UNIT,
      axis_title: `Temperature-Time Factor (${TTF_UNIT})`,
    };
  }
  const ts = numeric(specifiedTemp) ?? DEFAULT_SPECIFIED_TEMP;
  const label = `Equivalent Age at ${formatTemp(ts)} °C`;
  return {
    method: ARRHENIUS,
    kind: 'ea',
    index_short: 'EA',
    index_label: label,
    unit: EA_UNIT,
    axis_title: `${label} (${EA_UNIT})`,
    specified_temp: ts,
  };
};

/** Anything that can name a pour's maturity: a dashboard pour summary, a
 *  chart response, a descriptor, or a bare method string. */
export type MaturitySource =
  | {
      maturity?: MaturityDescriptor | null;
      maturity_method?: string | null;
    }
  | MaturityDescriptor
  | string
  | null
  | undefined;

const isDescriptor = (value: unknown): value is MaturityDescriptor =>
  !!value &&
  typeof value === 'object' &&
  ((value as MaturityDescriptor).kind === 'ttf' || (value as MaturityDescriptor).kind === 'ea') &&
  typeof (value as MaturityDescriptor).unit === 'string';

/**
 * The maturity naming for a pour: the backend descriptor when the source has
 * one, else the backend rule applied to its method (only "Nurse Saul" is
 * TTF; everything else, including a missing method, is EA at 20 °C).
 */
export const maturityMeta = (source?: MaturitySource): MaturityDescriptor => {
  if (typeof source === 'string') return describeMaturity(source);
  if (isDescriptor(source)) return source;
  if (source && typeof source === 'object') {
    const s = source as { maturity?: MaturityDescriptor | null; maturity_method?: string | null };
    if (isDescriptor(s.maturity)) return s.maturity;
    return describeMaturity(s.maturity_method);
  }
  return describeMaturity(undefined);
};

/** Maturity naming only when the method is known: null or undefined give
 *  null (e.g. a mix design with no maturity equation yet, or a Manual
 *  calibration without a basis). An Arrhenius method names `specifiedTemp`
 *  (20 °C when not given). */
export const knownMaturityMeta = (
  method?: string | null,
  specifiedTemp?: number | string | null,
): MaturityDescriptor | null =>
  method == null ? null : describeMaturity(method, specifiedTemp);

/** The specified temperature an equivalent age is at: the descriptor's, else
 *  the one its label names, else the 20 °C default. */
const specifiedTempOf = (meta: MaturityDescriptor): number => {
  const ts = numeric(meta.specified_temp);
  if (ts != null) return ts;
  const named = /at (-?\d+(?:\.\d+)?) °C/.exec(meta.index_label ?? '');
  return named ? Number(named[1]) : DEFAULT_SPECIFIED_TEMP;
};

/**
 * The compact maturity name for sub-lines, column headings and section
 * names: "TTF", or "EA at 20 °C" (an equivalent age always names its
 * specified temperature, ASTM C1074 X2.3.2).
 */
export const maturityShortLabel = (meta: MaturityDescriptor): string =>
  meta.kind === 'ea'
    ? `${meta.index_short} at ${formatTemp(specifiedTempOf(meta))} °C`
    : meta.index_short;

/** A compact heading naming the unit: "TTF (°C·hrs)" / "EA at 20 °C (hrs)". */
export const maturityShortTitle = (meta: MaturityDescriptor): string =>
  `${maturityShortLabel(meta)} (${meta.unit})`;

/** What goes before a maturity value in the compact form: "TTF " or
 *  "EA at 20 °C: " (the colon keeps the temperature apart from the value). */
export const maturityValuePrefix = (meta: MaturityDescriptor): string =>
  meta.kind === 'ea' ? `${maturityShortLabel(meta)}: ` : `${maturityShortLabel(meta)} `;

/** The maturity unit, or "TTF (°C·hrs) or EA (hrs)" when the method is not
 *  known (null / undefined). */
export const maturityUnitText = (method?: string | null): string =>
  knownMaturityMeta(method)?.unit ?? MATURITY_UNIT_UNKNOWN;

/** Column / field label for a maturity target: "TTF Target (°C·hrs)",
 *  "EA Target (hrs)", or "Maturity Target, TTF (°C·hrs) or EA (hrs)". */
export const maturityTargetLabel = (method?: string | null): string => {
  const meta = knownMaturityMeta(method);
  return meta ? `${meta.index_short} Target (${meta.unit})` : `Maturity Target, ${MATURITY_UNIT_UNKNOWN}`;
};

/** "1234.5 °C·hrs" / "120.4 hrs" (half up to 1 dp, as the backend's
 *  progress label and chart tooltips); `empty` when there is no value. */
export const formatMaturity = (
  value: number | null | undefined,
  meta: MaturityDescriptor,
  decimals = 1,
  empty = '-',
): string => {
  const v = finite(value);
  return v == null ? empty : `${formatHalfUp(v, decimals)} ${meta.unit}`;
};

/** "TTF 1234.5 °C·hrs" / "EA at 20 °C: 120.4 hrs" (half up to 1 dp);
 *  `empty` when there is no value. */
export const formatMaturityWithIndex = (
  value: number | null | undefined,
  meta: MaturityDescriptor,
  decimals = 1,
  empty = '-',
): string => {
  const v = finite(value);
  return v == null ? empty : `${maturityValuePrefix(meta)}${formatHalfUp(v, decimals)} ${meta.unit}`;
};

/** The first non-blank unit given, else the company default (MPa, as a
 *  blank StrengthFormat setting). */
export const resolveStrengthUnit = (...units: (string | null | undefined)[]): string => {
  for (const u of units) {
    if (u && u.trim()) return u.trim();
  }
  return strengthUnitFromFormat(null);
};

/** A field label naming its unit when known ('Strength Grade (Psi)'), else
 *  the bare label: a unit still loading is never guessed. */
export const labelWithUnit = (label: string, unit?: string | null): string => {
  const u = unit?.trim();
  return u ? `${label} (${u})` : label;
};

/** A value with its unit when known ('40 Psi'), else the bare value ('40');
 *  `empty` when there is no value. */
export const valueWithUnit = (
  value: number | string | null | undefined,
  unit?: string | null,
  empty = '-',
): string => {
  if (value == null || String(value).trim() === '') return empty;
  const u = unit?.trim();
  return u ? `${value} ${u}` : String(value);
};

/** Half-up to 1 dp, as the backend's round_1dp (24.25 -> 24.3). */
const round1 = (value: number): number => roundHalfUp(value, 1);

/** A target strength as text, half up to 1 dp with the unit when known
 *  ("32.0 Psi", or "32.0" while the unit loads); '' for no strength or one
 *  of 0 or less (a maturity-only target). */
export const targetStrengthText = (
  strength: number | string | null | undefined,
  unit?: string | null,
): string => {
  const s = numeric(strength);
  return s != null && s > 0 ? valueWithUnit(round1(s).toFixed(1), unit) : '';
};

/** A name that only repeats a value: older progress-bar responses name a
 *  target without a purpose by its strength ("32.0 MPa") or maturity
 *  ("120.0 hrs"). */
const VALUE_ONLY_NAME = /^-?\d+(?:\.\d+)?(?:\s*(?:MPa|Psi|N\/mm²|°C·hrs|hrs))?$/i;

/** The first of `names` that is a configured purpose, or null. Newer
 *  progress-bar responses send "Target" for a target without a purpose and
 *  older ones its value ("32.0 MPa"): neither counts. */
export const targetPurpose = (...names: (string | null | undefined)[]): string | null => {
  for (const name of names) {
    const text = (name ?? '').trim();
    if (text && text !== 'Target' && !VALUE_ONLY_NAME.test(text)) return text;
  }
  return null;
};

/**
 * A strength target's name for chart lines and progress markers, never
 * repeating the unit: "Initial Stressing (32.0 MPa)", "32.0 MPa" without a
 * purpose, the purpose alone without a strength, else `fallback`.
 */
export const targetDisplayName = (
  purpose: string | null | undefined,
  strength: number | string | null | undefined,
  unit?: string | null,
  fallback = 'Target',
): string => {
  const name = (purpose ?? '').trim();
  const value = targetStrengthText(strength, unit);
  if (name && value) return `${name} (${value})`;
  return name || value || fallback;
};

/**
 * The strength reading to show. Prefers the backend's reading; for older
 * responses builds one from the legacy fields: a value, or "< min" when the
 * value is missing and the lowest sample strength is known.
 * Returns null when there is nothing to show.
 */
export const resolveStrengthReading = (
  reading?: StrengthReading | null,
  legacyValue?: number | null,
  legacyMin?: number | null,
): StrengthReading | null => {
  if (reading) return reading;
  const v = finite(legacyValue);
  if (v != null) {
    return { value: round1(v), bound: null, no_curve: false, no_samples: false, display: round1(v).toFixed(1), tooltip: null };
  }
  const min = finite(legacyMin);
  if (min != null) {
    return {
      value: round1(min),
      bound: 'below',
      no_curve: false,
      no_samples: false,
      display: `< ${round1(min).toFixed(1)}`,
      tooltip: OUTSIDE_RANGE_TOOLTIP,
    };
  }
  return null;
};

/**
 * A strength reading as text: "24.3 MPa", "< 3.3 MPa", "> 41.0 MPa", "NCS*"
 * (never with a unit), or `empty` when there is nothing to show (no reading,
 * no curve, or no maturity yet).
 */
export const formatStrengthReading = (
  reading: StrengthReading | null | undefined,
  unit?: string | null,
  empty = '-',
): string => {
  const display = (reading?.display ?? '').trim();
  if (!reading || !display) return empty;
  if (display === NO_SAMPLES_DISPLAY || reading.value == null) return display;
  return unit ? `${display} ${unit}` : display;
};

/** The tooltip for a reading: the backend's, or the rule's text when a
 *  bounded / no-samples reading arrives without one. */
export const strengthReadingTooltip = (reading?: StrengthReading | null): string | null => {
  if (!reading) return null;
  if (reading.tooltip) return reading.tooltip;
  if (reading.bound) return OUTSIDE_RANGE_TOOLTIP;
  if (reading.no_samples && reading.display === NO_SAMPLES_DISPLAY) return NO_SAMPLES_TOOLTIP;
  return null;
};

/** Tooltip for a strength string the backend built (e.g. the progress-bar
 *  label): "< ..." and "> ..." are outside the tested range, "NCS*" has no
 *  calibration samples. */
export const strengthDisplayTooltip = (display?: string | null): string | null => {
  const text = (display ?? '').trim();
  if (/^[<>]/.test(text)) return OUTSIDE_RANGE_TOOLTIP;
  if (text.startsWith(NO_SAMPLES_DISPLAY)) return NO_SAMPLES_TOOLTIP;
  return null;
};

/**
 * Whether a target strength lies outside the tested range, as the backend's
 * CalibratedRange.target_flag: compared with the rounded S(min) / S(max), so
 * a target equal to either end is inside. Null when the strength or the
 * range is unknown.
 */
export const targetRangeFlag = (
  strength: number | null | undefined,
  range?: CalibratedRange | null,
): 'below' | 'above' | null => {
  const s = finite(strength);
  if (s == null || !range || !range.has_curve || !range.has_samples) return null;
  const lo = finite(range.s_min);
  const hi = finite(range.s_max);
  if (lo != null && s < lo) return 'below';
  if (hi != null && s > hi) return 'above';
  return null;
};

/**
 * Whether a target maturity lies outside the tested maturity range
 * [min_m, max_m], as the backend's CalibratedRange.maturity_flag: more than
 * TARGET_MATURITY_SLACK past either end. Null inside, and when the maturity
 * (0 or less), the curve or the samples are unknown.
 */
export const maturityRangeFlag = (
  maturity: number | null | undefined,
  range?: CalibratedRange | null,
): 'below' | 'above' | null => {
  const m = finite(maturity);
  if (m == null || m <= 0 || !range || !range.has_curve || !range.has_samples) return null;
  const lo = finite(range.min_m);
  const hi = finite(range.max_m);
  if (lo != null && m < lo - MATURITY_SLACK_COMPARED) return 'below';
  if (hi != null && m > hi + MATURITY_SLACK_COMPARED) return 'above';
  return null;
};

/** A target is flagged outside the tested range: the backend's flag when it
 *  sent one, else worked out from the calibrated range as the backend's
 *  CalibratedRange.target_outside: its strength outside [S(min), S(max)] or
 *  its maturity (target_ea on the chart, target_maturity on the dashboard)
 *  outside [min_m, max_m]. A target without a strength (<= 0) is flagged by
 *  its maturity only. */
export const isTargetOutsideRange = (
  target: {
    outside_range?: boolean | string | null;
    target_strength?: number | null;
    target_ea?: number | null;
    target_maturity?: number | null;
  },
  range?: CalibratedRange | null,
): boolean => {
  if (target.outside_range !== undefined && target.outside_range !== null) {
    return !!target.outside_range;
  }
  const s = finite(target.target_strength);
  if (s != null && s > 0 && targetRangeFlag(s, range) != null) return true;
  return maturityRangeFlag(target.target_ea ?? target.target_maturity, range) != null;
};

/** Whether the pour has a usable calibration curve: the backend's has_curve
 *  when sent, else has_calibration, else undefined (unknown). */
export const pourHasCurve = (
  pour?: { has_curve?: boolean | null; has_calibration?: boolean | null } | null,
): boolean | undefined => {
  if (pour?.has_curve != null) return pour.has_curve;
  if (pour?.has_calibration != null) return pour.has_calibration;
  return undefined;
};
